import { useCallback, useMemo } from 'react';
import { AutocompleteInputChangeReason } from '@mui/material';
import { AutocompleteOption } from '@/common/presentation/hooks/useAutocomplete';
import { Dtos_V1_VendorDto } from '@/common/infrastructure/gql/generated/schema';

interface UseVendorAutocompleteProps {
  availableVendors?: Dtos_V1_VendorDto[];
  loading?: boolean;
  rawVendor?: Dtos_V1_VendorDto | null;
  selectedVendorId?: number | null;
  onRemove?: () => void;
}

interface UseVendorAutocompleteReturn {
  vendorOptions: AutocompleteOption[];
  vendorFilterOptions: (options: AutocompleteOption[], state: { inputValue: string }) => AutocompleteOption[];
  handleVendorInputChange: (_: unknown, _value: string, _reason?: AutocompleteInputChangeReason) => void;
  initialVendorOption?: AutocompleteOption;
  selectedVendor: Dtos_V1_VendorDto | null;
  handleRemoveVendor: () => void;
  vendorMap: Map<number, Dtos_V1_VendorDto>;
  loading: boolean;
}

/**
 * Hook for managing vendor autocomplete functionality
 *
 * Encapsulates all vendor-related autocomplete logic including:
 * - Options transformation and memoization
 * - Filtering logic
 * - Initial option calculation
 * - Selected vendor lookup
 * - Remove handler
 *
 * @param props - Configuration object
 * @param props.availableVendors - Array of available vendors from form options
 * @param props.loading - Loading state for vendors
 * @param props.rawVendor - Raw vendor data from job (for initial option)
 * @param props.selectedVendorId - Currently selected vendor ID from form values
 * @param props.onRemove - Optional callback when vendor is removed
 * @returns Object containing all vendor autocomplete utilities
 *
 * @example
 * ```tsx
 * const {
 *   vendorOptions,
 *   vendorFilterOptions,
 *   handleVendorInputChange,
 *   initialVendorOption,
 *   selectedVendor,
 *   handleRemoveVendor,
 *   loading
 * } = useVendorAutocomplete({
 *   availableVendors,
 *   loading: vendorsLoading,
 *   rawVendor: rawJob?.vendor,
 *   selectedVendorId: values.vendorId,
 *   onRemove: () => setFieldValue('vendorId', null)
 * });
 * ```
 */
export const useVendorAutocomplete = ({
  availableVendors = [],
  loading = false,
  rawVendor,
  selectedVendorId,
  onRemove
}: UseVendorAutocompleteProps): UseVendorAutocompleteReturn => {
  const vendorOptions = useMemo(() => {
    if (!availableVendors || availableVendors.length === 0) return [];
    return availableVendors.map((vendor) => ({
      value: vendor.id,
      label: vendor.name ?? 'Unknown Vendor'
    }));
  }, [availableVendors]);

  const vendorMap = useMemo(() => {
    if (!availableVendors || availableVendors.length === 0) return new Map<number, Dtos_V1_VendorDto>();
    return new Map(availableVendors.map((vendor) => [vendor.id, vendor]));
  }, [availableVendors]);

  const vendorFilterOptions = useCallback((options: AutocompleteOption[], state: { inputValue: string }) => {
    if (!state.inputValue) return options;
    const searchLower = state.inputValue.toLowerCase();
    return options.filter((option) => option.label?.toLowerCase().includes(searchLower) ?? false);
  }, []);

  const handleVendorInputChange = useCallback((_: unknown, _value: string, _reason?: AutocompleteInputChangeReason) => {}, []);

  const initialVendorOption = useMemo(() => {
    if (rawVendor) {
      return {
        value: rawVendor.id ?? 0,
        label: rawVendor.name ?? 'Unknown Vendor'
      };
    }
    return undefined;
  }, [rawVendor]);

  const selectedVendor = useMemo(() => {
    if (rawVendor && rawVendor.id != null && rawVendor.id === selectedVendorId) {
      return rawVendor;
    }

    if (rawVendor && (selectedVendorId == null || selectedVendorId === 0)) {
      return rawVendor;
    }

    if (
      selectedVendorId != null &&
      selectedVendorId !== 0 &&
      rawVendor &&
      rawVendor.id != null &&
      rawVendor.id !== selectedVendorId &&
      vendorMap.size > 0
    ) {
      const vendorFromMap = vendorMap.get(selectedVendorId);
      if (vendorFromMap) {
        return vendorFromMap;
      }
    }

    if (selectedVendorId != null && selectedVendorId !== 0 && !rawVendor && vendorMap.size > 0) {
      const vendorFromMap = vendorMap.get(selectedVendorId);
      if (vendorFromMap) {
        return vendorFromMap;
      }
    }

    if (rawVendor) {
      return rawVendor;
    }

    return null;
  }, [selectedVendorId, rawVendor, vendorMap]);

  const handleRemoveVendor = useCallback(() => {
    onRemove?.();
  }, [onRemove]);

  return {
    vendorOptions,
    vendorFilterOptions,
    handleVendorInputChange,
    initialVendorOption,
    selectedVendor,
    handleRemoveVendor,
    vendorMap,
    loading
  };
};
